import { useDispatch, useSelector } from 'react-redux';
import { useBooleanFlagValue } from '@rsos/feature-flags';
import useHasAccessToAirAmbulance from '@rsos/hooks/useHasAccessToAirAmbulance';
import useHasAccessToIntlCallback from '@rsos/hooks/useHasAccessToIntlCallback';
import { OVERVIEW_TAB } from '@rsos/reduxDataStore/src/features/eventPane/eventPaneTabs';
import {
  openResourcePanel,
  setResourcePanelView,
  setSelectedRequest,
} from '@rsos/reduxDataStore/src/features/resourceRequests';
import { getMostRecentLoc } from '@rsos/utils/getMostRecentLoc';
import isValidCoordinates from '@rsos/utils/isValidCoordinates';
import {
  trackRequestAirAmbulanceClick,
  AIR_AMBULANCE_SOURCE_BUTTON,
} from '../../../../../gainsightTrackings/airAmbulanceTrackings';
import useHasAccessToCommunicatorTranscription from '../../../../../hooks/useHasAccessToCommunicatorTranscription';
import useIsFordCall from '../../../../../hooks/useIsFordCall';
import AdrQueryContainer from '../../../../AdditionalData/AdrQuery/AdrQueryContainer';
import MediaUniteEventPane from '../../../../AdditionalData/Media/MediaUniteEventPane';
import MostRecentLocationCard from '../../../../MostRecentLocationCard';
import {
  isUnplottableAddress,
  UNGEOCODED_CALLS_FLAG,
} from '../../../Map/unplottableAddress';
import AirAmbulanceCard from '../../AirAmbulance';
import ChatCard from '../../ChatCard';
import EventShareCard from '../../EventShareCard/EventShareCard';
import { OverviewIntlCallbackCard } from '../../IntlCallback';
import PreviousCallsCard from '../../PreviousCallsCard';
import DataCardsSummary from '../DataCardsSummary';
import { selectTranscriptionTotalForCall } from '../TranscriptTab/transcriptionSlice';
import OverviewBanners from './OverviewBanners';
import { OverviewTabContent, FullWidthContent } from './OverviewTab.styles';

/**
 * The Overview tab of the EventPane
 */
const OverviewTab = ({
  onDataCardClick,
  callerID,
  onOpenRecentTranscripts,
}) => {
  const hasAccessToAirAmbulance = useHasAccessToAirAmbulance();
  const hasAccessToIntlCallback = useHasAccessToIntlCallback();
  const isFord = useIsFordCall(callerID);
  const showRecentTranscripts = useBooleanFlagValue(
    'show_recent_transcripts',
    false,
  );
  const hasAccessToCommunicatorTranscript =
    useHasAccessToCommunicatorTranscription();

  const selectedCall = useSelector(
    state => state.emergencyCalls?.callQueue?.[callerID],
  );

  const totalTranscriptionCount = useSelector(state =>
    showRecentTranscripts
      ? selectTranscriptionTotalForCall(callerID)(state)
      : 0,
  );
  const canShowPreviousCalls =
    !isFord &&
    showRecentTranscripts &&
    hasAccessToCommunicatorTranscript &&
    totalTranscriptionCount > 0;

  const mostRecentLoc = getMostRecentLoc(selectedCall);
  const hasLocationToRender = isValidCoordinates(mostRecentLoc);
  const ungeocodedCallsEnabled = useBooleanFlagValue(
    UNGEOCODED_CALLS_FLAG,
    false,
  );
  const hasUnplottableAddress =
    ungeocodedCallsEnabled && isUnplottableAddress(mostRecentLoc);

  const dispatch = useDispatch();
  const handleAirAmbulanceResourcePanel = () => {
    dispatch(setSelectedRequest(null));
    dispatch(openResourcePanel());
    dispatch(setResourcePanelView('air_ambulance'));
    trackRequestAirAmbulanceClick({
      sourceButton: AIR_AMBULANCE_SOURCE_BUTTON.OVERVIEW_TAB,
    });
  };

  return (
    <>
      <OverviewTabContent horizontal>
        <OverviewBanners />
        <AdrQueryContainer />
        {!selectedCall?.isAdmin &&
          (hasLocationToRender || hasUnplottableAddress) && (
            <MostRecentLocationCard />
          )}
        {canShowPreviousCalls && (
          <FullWidthContent
            backgroundColor="background"
            data-name="overview-previous-calls-card"
            noPadding
          >
            <PreviousCallsCard
              totalCallsCount={totalTranscriptionCount}
              onClick={onOpenRecentTranscripts}
            />
          </FullWidthContent>
        )}
        <DataCardsSummary onDataCardClick={onDataCardClick} />
        <EventShareCard />
        <ChatCard />
        <MediaUniteEventPane />
        {hasAccessToAirAmbulance && (
          <AirAmbulanceCard
            tab={OVERVIEW_TAB}
            handleClick={handleAirAmbulanceResourcePanel}
          />
        )}
        {hasAccessToIntlCallback && !isFord && <OverviewIntlCallbackCard />}
      </OverviewTabContent>
    </>
  );
};

export default OverviewTab;
